<template>
  <component :is="currentComponent" v-bind="{ ...properties, abTesting, originalComponent }" />
</template>
<script>
import Category from '../components/Blog/Category';
import Page from '../components/Page';
import LandingPage from '../components/LandingPage';
import LandingPageV2 from '../components/LandingPageV2';
import AffiliatePage from '../components/AffiliatePage';
import CoursePage from '../components/CourseSale/CoursePage';
import DiscoverPage from '../components/DiscoverPage';
import AlternativePage from '../components/AlternativePage';
import ABWrapper from '../components/ABWrapper';

import {
  LANDING_PAGES, SELLABLE_COURSES, SELLABLE_COURSES_PROMOTIONS, AFFILIATE_PAGES, DISCOVER_PAGES, AB_TESTING_PAGES, ALTERNATIVE_PAGES_URLS,
} from '../utils/constants';

export default {
  components: {
    Category, Page, LandingPage, LandingPageV2, AffiliatePage, CoursePage, DiscoverPage, ABWrapper, AlternativePage,
  },
  async asyncData({
    $axios, route, error, store, redirect, $config, req,
  }) {
    let data = {};
    const url = route.path.replace(/\//g, '').toLowerCase();
    const urlFull = route.fullPath.replace(/\//g, '');
    const ABTesting = AB_TESTING_PAGES.find((p) => p.slug === url) || {};

    const landingPagesUrls = LANDING_PAGES.map((lp) => lp.replace(/\//g, ''));
    const coursePagesUrls = SELLABLE_COURSES.map((lp) => lp.replace(/\//g, ''));
    const affiliatePagesUrls = AFFILIATE_PAGES.map((lp) => lp.replace(/\//g, ''));
    const discoverPagesUrls = DISCOVER_PAGES.map((lp) => lp.replace(/\//g, ''));
    const alternavitePagesUrls = ALTERNATIVE_PAGES_URLS[$config.platform].map((lp) => lp.replace(/\//g, ''));

    if (url.startsWith('answer-for')) {
      const question = await $axios.$get(`/qa-hub/question-slug-by-answer/${url}`).catch(() => false);
      if (question !== false) {
        redirect(`/question/${question.slug}/#answer-${question.answerId}`);
      }
    }

    // LANDING PAGES
    if (landingPagesUrls.includes(url)) {
      let userInfo = {};
      if (url === 'upgrade-offer' && store.getters['auth/user'].role !== 'admin') {
        if (route.query.link || store.state.auth.user) {
          userInfo = await $axios.$get(`/user/info?link=${route.query.link}`).catch(() => {});

          if (!userInfo) {
            redirect('/you-missed-it/');
          }

          const registeredAt = new Date(userInfo.registeredAt);
          if (new Date(registeredAt.setDate(registeredAt.getDate() + userInfo.offerDuration || 5)) < new Date()) {
            redirect('/you-missed-it/');
          }
        } else {
          redirect('/you-missed-it/');
        }
      }

      if (url === 'start-learning') {
        const cookies = (req?.headers?.cookie || '').split(';').reduce((p, c) => {
          const [k, v] = c.split('=');

          return { ...p, [k.trim()]: decodeURIComponent(v) };
        }, { });

        if (cookies['timer-yt-campaign'] && cookies['timer-yt-campaign'] < Date.now() - (1000 * 60 * 60)) {
          redirect('/offer-expired/');
        }
      }

      let currentComponent = 'LandingPage';
      const getParams = ['prices=1'];

      if (url === 'start-learning' && $config.platform === 'ds') {
        getParams.push('courses=1');
      }

      const seo = await $axios.$get(`/pages/${url}?${getParams.join('&')}`).catch(() => error({ statusCode: 404, message: 'Page not found' }));

      if (url === 'start-learning' && $config.platform === 'ds') {
        currentComponent = 'LandingPageV2';
      }

      data = { currentComponent, properties: { seo, userInfo, url } };
    }

    // Course pages
    if (coursePagesUrls.includes(url)) {
      const { coupon = '' } = route.query;
      const { startDate, endDate } = SELLABLE_COURSES_PROMOTIONS[url] || {};
      const date = new Date();
      const params = [];

      if (coupon) {
        params.push(`coupon=${coupon}`);
      }

      if (date < new Date(startDate) || date > new Date(endDate)) {
        params.push('stoppromo=1');
      }

      const [course, seo, stats] = await Promise.all([
        $axios.$get(`/courses/landing/${url}${params.length ? `?${params.join('&')}` : ''}`).catch(() => { }),
        $axios.$get(`/pages/${url}`).catch(() => error({ statusCode: 404, message: 'Page not found' })),
        $axios.$get('/stats/courses?reduced=1').catch(() => { }),
      ]);

      if (!course) {
        return error({ statusCode: 404, message: 'Course not found' });
      }

      data = {
        currentComponent: 'CoursePage',
        properties: { seo, courseInfo: { ...course, promotion: { startDate, endDate } }, stats },
      };
    }

    // Affiliate pages
    if (affiliatePagesUrls.includes(url)) {
      if ($config.platform === 'fa') {
        return error({ statusCode: 404, message: 'Page not found' });
      }

      const [instructor, stats, seo] = await Promise.all([
        await $axios.$get(`/team/landing/${url}`).catch(() => error({ statusCode: 404, message: 'Instructor not found' })),
        await $axios.$get('/stats/courses?reduced=1').catch(() => ({})),
        await $axios.$get(`/pages/${url}?prices=1`).catch(() => error({ statusCode: 404, message: 'Page not found' })),
      ]);

      data = {
        currentComponent: 'AffiliatePage',
        properties: {
          seo, instructor, stats, url,
        },
      };
    }

    // Alternative pages
    if (alternavitePagesUrls.includes(url)) {
      const seo = await $axios.$get(`/pages/${url}?prices=1&alternative=1`).catch(() => error({ statusCode: 404, message: 'Page not found' }));

      data = {
        currentComponent: 'AlternativePage',
        properties: { seo },
      };
    }

    // Discover pages
    if (discoverPagesUrls.includes(url)) {
      const seo = await $axios.$get(`/pages/${url}${route.query.resourcesCategory ? `?filter[resourcesCategory]=${route.query.resourcesCategory}` : ''}`).catch(() => error({ statusCode: 404, message: 'Page not found' }));

      data = { currentComponent: 'DiscoverPage', properties: { seo } };
    }

    // Blog category
    if (!data.currentComponent) {
      const categoryResponse = await $axios.$get(`/blog/categories/${urlFull}`, { noError: true }).catch(() => false);
      if (categoryResponse && categoryResponse.parent) {
        return error({ statusCode: 404, message: 'Category not found' });
      }

      if (categoryResponse) {
        const seo = await $axios.$get(`/pages/${urlFull}`).catch(() => error({ statusCode: 404, message: 'Page not found' }));

        if (!seo) {
          return error({ statusCode: 404, message: 'Page not found' });
        }

        data = { currentComponent: 'Category', properties: { info: { ...categoryResponse, ...seo, categoryId: categoryResponse.id } } };
      }
    }

    // Blog article
    if (!data.currentComponent) {
      const pageResponse = await $axios.$get(`/pages/${urlFull}?filter[locked]=0`).catch(() => false);
      if (pageResponse) {
        data = { currentComponent: 'Page', properties: { info: pageResponse } };
      }
    }

    if (data.currentComponent) {
      if (ABTesting.slug) {
        data = {
          ...data,
          currentComponent: 'ABWrapper',
          originalComponent: data.currentComponent,
          abTesting: ABTesting || {},
        };
      }

      return {
        abTesting: {},
        originalComponent: '',
        ...data,
      };
    }

    return error({ statusCode: 404, message: 'Page not found' });
  },
  head() {
    return {
      titleTemplate: 'Page not found%s',
    };
  },
};
</script>
